import { useEffect, useState, useCallback } from "react";
import { Modal, ModalBody, Spinner } from "reactstrap";
import CircularProgress from "../../../../components/circular-progress/circular-progress";
import PlansAPI from "../../../../services/api/plans.api";

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const formatCurrency = (amount, currency) => {
  if (amount == null || currency == null) return "—";
  const formatted = new Intl.NumberFormat("es-CL").format(amount);
  return `$${formatted} ${currency}`;
};

const statusConfig = {
  ACTIVE: { label: "Activo", className: "gm-plan-status--active" },
  EXPIRED: { label: "Expirado", className: "gm-plan-status--expired" },
};

const ClientPlansModal = ({ open, toggle, clientId }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const fetchPlans = useCallback(() => {
    if (!clientId) return;
    setLoading(true);
    setError(false);
    PlansAPI.getClientPlans(clientId)
      .then((res) => setPlans(res.data))
      .catch(() => {
        setPlans([]);
        setError(true);
      })
      .finally(() => setLoading(false));
  }, [clientId]);

  useEffect(() => {
    let isMounted = true;

    if (open && clientId) {
      setLoading(true);
      setError(false);
      PlansAPI.getClientPlans(clientId)
        .then((res) => {
          if (isMounted) setPlans(res.data);
        })
        .catch(() => {
          if (isMounted) {
            setPlans([]);
            setError(true);
          }
        })
        .finally(() => {
          if (isMounted) setLoading(false);
        });
    }

    return () => {
      isMounted = false;
    };
  }, [open, clientId]);

  const getStatusInfo = (status) =>
    statusConfig[status] || { label: status, className: "" };

  return (
    <Modal isOpen={open} toggle={toggle} size="xl" centered>
      <div className="gm-modal-header">
        <div className="gm-modal-title-container">
          <div className="gm-modal-title">Mis Planes</div>
        </div>
        <button
          type="button"
          className="btn-close"
          aria-label="Close"
          onClick={toggle}
        />
      </div>
      <ModalBody>
        <div className="gm-modal-plans-container">
          {loading ? (
            <div className="gm-modal-plans-loading">
              <Spinner />
            </div>
          ) : error ? (
            <div className="gm-modal-plans-error">
              <p>Error al cargar los planes.</p>
              <button
                type="button"
                className="gm-outline-button"
                onClick={fetchPlans}
              >
                Reintentar
              </button>
            </div>
          ) : plans.length === 0 ? (
            <div className="gm-modal-plans-empty">
              No hay planes registrados para este cliente.
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Transacción</th>
                  <th>Producto</th>
                  <th>Fecha</th>
                  <th>Vigencia</th>
                  <th>Valor</th>
                  <th>Tests restantes</th>
                  <th>Total tests</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {plans.map((plan) => {
                  const statusInfo = getStatusInfo(plan.status);
                  return (
                    <tr
                      key={plan.id}
                      className={
                        plan.status === "EXPIRED" ? "gm-plan-row--expired" : ""
                      }
                    >
                      <td className="gm-plan-transaction">{plan.gatewayTransactionId ?? "—"}</td>
                      <td>{plan.planName}</td>
                      <td>{formatDate(plan.purchaseDate)}</td>
                      <td className="gm-plan-vigencia">
                        <CircularProgress
                          daysRemaining={plan.daysRemaining}
                          totalDays={plan.totalDays}
                        />
                      </td>
                      <td>{formatCurrency(plan.amount, plan.currency)}</td>
                      <td className="gm-plan-tests">{plan.testsRemaining}</td>
                      <td className="gm-plan-tests">{plan.testsTotal}</td>
                      <td>
                        <span
                          className={`gm-plan-status ${statusInfo.className}`}
                        >
                          {statusInfo.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};

export default ClientPlansModal;
